import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import AppShell from "../../components/AppShell";
import { api } from "../../lib/api";
import { Alert, Card, Field, btn, input, primary } from "../../components/workforce/common";
import ProjectShares from "../../components/comms/ProjectShares";

const msg = (e) => e?.response?.data?.detail?.message || "Something went wrong.";
const SRC = { manual: "Logged", outbox: "Sent by the system", eml: "Uploaded email", m365: "Microsoft 365" };

function LogForm({ pid, onDone }) {
  const [f, setF] = useState({ kind: "call", subject: "", summary: "", occurred_at: new Date().toISOString().slice(0, 16) });
  const [err, setErr] = useState("");
  const save = () => api.post("/comms/log", { ...f, occurred_at: new Date(f.occurred_at).toISOString(), project_id: pid })
    .then(() => { setF({ ...f, subject: "", summary: "" }); setErr(""); onDone(); }).catch((e) => setErr(msg(e)));
  return (
    <Card testid="cl-form" className="grid gap-2 sm:grid-cols-4 items-end">
      <Field id="cl-kind" label="Type"><select id="cl-kind" className={input} value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} data-testid="cl-kind">
        {["call", "meeting", "email", "letter"].map((k) => <option key={k} value={k}>{k}</option>)}</select></Field>
      <Field id="cl-when" label="When"><input id="cl-when" type="datetime-local" className={input} value={f.occurred_at} onChange={(e) => setF({ ...f, occurred_at: e.target.value })} data-testid="cl-when" /></Field>
      <Field id="cl-subject" label="Subject"><input id="cl-subject" className={input} value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} data-testid="cl-subject" /></Field>
      <button type="button" className={primary} onClick={save} data-testid="cl-save">Log it</button>
      <Field id="cl-summary" label="Notes"><textarea id="cl-summary" className={`${input} sm:col-span-4`} value={f.summary} onChange={(e) => setF({ ...f, summary: e.target.value })} data-testid="cl-summary" /></Field>
      <Alert text={err} testid="cl-error" />
    </Card>
  );
}

function EmlUpload({ pid, onDone }) {
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");
  const [code, setCode] = useState("");
  const [m365, setM365] = useState(null);
  useEffect(() => { api.get("/comms/m365/status").then((r) => setM365(r.data)).catch(() => {}); }, []);
  const upload = (file) => {
    const fd = new FormData(); fd.append("file", file); fd.append("project_id", pid);
    api.post("/comms/emails/eml", fd).then((r) => { setRes(r.data); setErr(""); onDone(); }).catch((e) => setErr(msg(e)));
  };
  return (
    <Card testid="cl-eml" className="space-y-2">
      <h2 className="font-heading text-lg">Add an email</h2>
      <p className="text-xs">Upload a saved .eml file (max 10 MB). It is shown as plain text; allowed attachments are stored securely.</p>
      <label htmlFor="cl-eml-file" className={`${btn} inline-flex items-center cursor-pointer`}>Choose .eml file</label>
      <input id="cl-eml-file" type="file" accept=".eml,message/rfc822" className="sr-only" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} data-testid="cl-eml-file" />
      {res && <p data-testid="cl-eml-result">Linked: {res.subject || "(no subject)"} · {res.attachment_ids.length} attachment(s) stored{res.skipped_attachments.length ? `, ${res.skipped_attachments.length} skipped` : ""}</p>}
      <div className="flex flex-wrap items-end gap-2">
        <Field id="cl-code" label="Project email code"><input id="cl-code" className={input} value={code} onChange={(e) => setCode(e.target.value)} data-testid="cl-code" /></Field>
        <button type="button" className={btn} onClick={() => api.put(`/comms/projects/${pid}/email-code`, { code }).then((r) => setErr(r.data.rule)).catch((e) => setErr(msg(e)))} data-testid="cl-code-save">Save code</button>
      </div>
      {m365 && <p className="text-xs font-semibold" data-testid="cl-m365">{m365.label} — {m365.note}</p>}
      <Alert text={err} testid="cl-eml-msg" />
    </Card>
  );
}

export default function ProjectComms() {
  const { pid } = useParams();
  const [rows, setRows] = useState([]);
  const [kind, setKind] = useState("");
  const [err, setErr] = useState("");
  const load = useCallback(() => api.get(`/comms/projects/${pid}/log`, { params: { kind: kind || undefined } }).then((r) => setRows(r.data)).catch((e) => setErr(msg(e))), [pid, kind]);
  useEffect(() => { load(); }, [load]);
  return (
    <AppShell title="Communications" subtitle="Emails, calls, meetings and letters for this project">
      <div className="mx-auto w-full max-w-[1000px] px-4 space-y-4" data-testid="cl-page">
        <LogForm pid={pid} onDone={load} />
        <EmlUpload pid={pid} onDone={load} />
        <div className="flex items-end gap-2"><h1 className="font-heading text-xl mr-auto">Log</h1>
          <label htmlFor="cl-filter" className="text-sm">Type</label>
          <select id="cl-filter" className={`${input} w-auto`} value={kind} onChange={(e) => setKind(e.target.value)} data-testid="cl-filter"><option value="">All</option>
            {["email", "call", "meeting", "letter"].map((k) => <option key={k} value={k}>{k}</option>)}</select></div>
        <Alert text={err} testid="cl-load-error" />
        <ul className="space-y-2">{rows.map((r) => (
          <li key={r.id} className="rounded border border-erf-borderCream bg-white p-3 text-sm" data-testid={`cl-row-${r.id}`}>
            <div className="flex flex-wrap justify-between gap-2"><strong>{r.kind} · {r.subject}</strong><span className="text-xs">{SRC[r.source]} · {(r.occurred_at || "").slice(0, 16).replace("T", " ")}</span></div>
            {r.delivery_state && <p className="text-xs" data-testid={`cl-delivery-${r.id}`}>Delivery: {r.delivery_state.replace(/_/g, " ")}</p>}
            {r.summary && <p className="whitespace-pre-wrap">{r.summary}</p>}
          </li>))}</ul>
        <ProjectShares pid={pid} />
      </div>
    </AppShell>
  );
}
